/**
 * GLSL ES 3.00 snippets shared by the passes. Every shader string starts with `#version 300 es` on its own
 * first line; these chunks are spliced in after the precision qualifiers.
 */

export const GLSL_CONSTS = /* glsl */ `
const float PI = 3.14159265358979;
const float TAU = 6.28318530717959;
`;

export const GLSL_HASH_NOISE = /* glsl */ `
// Dave Hoskins' hash12 on a lattice wrapped every 1024 units: the noise stays precise however long
// the session runs (time-scrolled coordinates never reach magnitudes where float hashing breaks down).
float hash21(vec2 p) {
  p = mod(p, 1024.0);
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
// 3 octaves (PLAN §4.4: ≤ 3 fbm octaves in the full-res pass)
float fbm3(vec2 p) {
  float v = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 3; i++) {
    v += amp * vnoise(p);
    p = p * 2.03 + vec2(17.1, 9.7);
    amp *= 0.5;
  }
  return v;
}
`;

export const GLSL_SDF = /* glsl */ `
float ndot(vec2 a, vec2 b) { return a.x * b.x - a.y * b.y; }
float sdCircle(vec2 p, float r) { return length(p) - r; }
float sdBox(vec2 p, vec2 b) {
  vec2 d = abs(p) - b;
  return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
}
float sdRhombus(vec2 p, vec2 b) {
  p = abs(p);
  float h = clamp((-2.0 * ndot(p, b) + ndot(b, b)) / dot(b, b), -1.0, 1.0);
  float d = length(p - 0.5 * b * vec2(1.0 - h, 1.0 + h));
  return d * sign(p.x * b.y + p.y * b.x - b.x * b.y);
}
// vesica with its long axis along y; r = circle radius, d = half distance between centres (r > d > 0)
float sdVesica(vec2 p, float r, float d) {
  p = abs(p);
  float b = sqrt(max(r * r - d * d, 1e-6));
  return ((p.y - b) * d > p.x * b) ? length(p - vec2(0.0, b)) : length(p - vec2(-d, 0.0)) - r;
}
float sdHexagon(vec2 p, float r) {
  const vec3 k = vec3(-0.866025404, 0.5, 0.577350269);
  p = abs(p);
  p -= 2.0 * min(dot(k.xy, p), 0.0) * k.xy;
  p -= vec2(clamp(p.x, -k.z * r, k.z * r), r);
  return length(p) * sign(p.y);
}
float sdSegment(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a;
  vec2 ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h);
}
float sdEllipseApprox(vec2 p, vec2 ab) { return (length(p / ab) - 1.0) * min(ab.x, ab.y); }
// a bird-like glyph (body, two swept wings, tail) for the Escher metamorphosis; s = size
float sdBird(vec2 p, float s) {
  float body = sdEllipseApprox(p, vec2(0.5, 0.2) * s);
  float wing1 = sdSegment(p, vec2(0.05, 0.0) * s, vec2(-0.3, 0.55) * s) - 0.075 * s;
  float wing2 = sdSegment(p, vec2(0.05, 0.0) * s, vec2(-0.3, -0.55) * s) - 0.075 * s;
  float tail = sdSegment(p, vec2(-0.45, 0.0) * s, vec2(-0.78, 0.0) * s) - 0.05 * s;
  float head = sdCircle(p - vec2(0.5, 0.05) * s, 0.13 * s);
  return min(min(body, head), min(min(wing1, wing2), tail));
}
// coverage of a stroke of half-width w around the zero set of d, anti-aliased over aa
float stroke(float d, float w, float aa) { return 1.0 - smoothstep(w - aa, w + aa, abs(d)); }
float fillIn(float d, float aa) { return 1.0 - smoothstep(-aa, aa, d); }
float glowOf(float d, float k) { return exp(-max(d, 0.0) * k); }
// kaleidoscopic folding: angle → 0..w/2 mirrored inside each of n cells (cell 0 centred on angle 0)
float fold(float a, float n) {
  float w = TAU / n;
  return abs(mod(a + w * 0.5, w) - w * 0.5);
}
float foldSigned(float a, float n) {
  float w = TAU / n;
  return mod(a + w * 0.5, w) - w * 0.5;
}
vec2 rot(vec2 p, float a) {
  float c = cos(a), s = sin(a);
  return vec2(c * p.x - s * p.y, s * p.x + c * p.y);
}
`;

export const GLSL_JWST = /* glsl */ `
// JWST ramp #05030D → #1B1F5E → #4E2A84 → #C2185B → #F28C28 → #FFF1C1
vec3 jwst(float t) {
  t = clamp(t, 0.0, 1.0) * 5.0;
  vec3 c = vec3(0.020, 0.012, 0.051);
  c = mix(c, vec3(0.106, 0.122, 0.369), clamp(t, 0.0, 1.0));
  c = mix(c, vec3(0.306, 0.165, 0.518), clamp(t - 1.0, 0.0, 1.0));
  c = mix(c, vec3(0.761, 0.094, 0.357), clamp(t - 2.0, 0.0, 1.0));
  c = mix(c, vec3(0.949, 0.549, 0.157), clamp(t - 3.0, 0.0, 1.0));
  c = mix(c, vec3(1.000, 0.945, 0.757), clamp(t - 4.0, 0.0, 1.0));
  return c;
}
`;
